import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { postStockTransfer } from "@/lib/inventory.functions";
import {
  getTransfer, listTransferLines, listTransferSkus, listTransferLocations,
  addTransferLine, removeTransferLine, cancelTransfer, deleteTransfer,
} from "@/lib/transfers.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2, ArrowLeft, PackageCheck, Trash2, X, Trash } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/transfers/$id")({ component: TransferDetailPage });

function TransferDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { tenantId, facilities } = useScope();
  const postTransferFn = useServerFn(postStockTransfer);
  const getTransferFn = useServerFn(getTransfer);
  const listLinesFn = useServerFn(listTransferLines);
  const listSkusFn = useServerFn(listTransferSkus);
  const listLocsFn = useServerFn(listTransferLocations);
  const addLineFn = useServerFn(addTransferLine);
  const removeLineFn = useServerFn(removeTransferLine);
  const cancelFn = useServerFn(cancelTransfer);
  const deleteFn = useServerFn(deleteTransfer);

  const [open, setOpen] = useState(false);
  const [skuId, setSkuId] = useState("");
  const [srcLoc, setSrcLoc] = useState("");
  const [dstLoc, setDstLoc] = useState("");
  const [lot, setLot] = useState("");
  const [qty, setQty] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [posting, setPosting] = useState(false);

  const { data: st } = useQuery<any>({
    queryKey: ["stock-transfer", id],
    queryFn: () => getTransferFn({ data: { id } }),
  });

  const { data: lines = [] } = useQuery<any[]>({
    queryKey: ["stock-transfer-lines", id],
    queryFn: () => listLinesFn({ data: { id } }),
  });

  const { data: skus = [] } = useQuery<any[]>({
    queryKey: ["transfer-skus", st?.tenant_id],
    enabled: !!st?.tenant_id,
    queryFn: () => listSkusFn({ data: { tenantId: st!.tenant_id } }),
  });

  const { data: srcLocs = [] } = useQuery<any[]>({
    queryKey: ["transfer-src-locs", st?.source_facility_id],
    enabled: !!st?.source_facility_id,
    queryFn: () => listLocsFn({ data: { facilityId: st!.source_facility_id } }),
  });

  const { data: dstLocs = [] } = useQuery<any[]>({
    queryKey: ["transfer-dst-locs", st?.dest_facility_id],
    enabled: !!st?.dest_facility_id,
    queryFn: () => listLocsFn({ data: { facilityId: st!.dest_facility_id } }),
  });

  const skuMap = useMemo(() => Object.fromEntries(skus.map((s) => [s.id, s])), [skus]);
  const allLocs = useMemo(() => [...srcLocs, ...dstLocs], [srcLocs, dstLocs]);
  const locMap = useMemo(() => Object.fromEntries(allLocs.map((l) => [l.id, l.code])), [allLocs]);
  const facMap = Object.fromEntries(facilities.map((f) => [f.id, f.name]));

  const editable = st?.status === "draft";

  const addLine = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = Number(qty);
    if (!skuId || !srcLoc || !dstLoc || !Number.isFinite(q) || q <= 0) return toast.error("Fill all fields with qty > 0");
    setSubmitting(true);
    try {
      await runServerFn(() => addLineFn({ data: {
        transferId: id, skuId, sourceLocationId: srcLoc, destLocationId: dstLoc,
        lot: lot.trim() || null, qty: q,
      } }), "Add line");
      toast.success("Line added");
      setOpen(false); setSkuId(""); setSrcLoc(""); setDstLoc(""); setLot(""); setQty("");
      qc.invalidateQueries({ queryKey: ["stock-transfer-lines", id] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const removeLine = async (lineId: string) => {
    try {
      await runServerFn(() => removeLineFn({ data: { lineId } }), "Remove line");
      qc.invalidateQueries({ queryKey: ["stock-transfer-lines", id] });
    } catch { /* toast handled */ }
  };

  const post = async () => {
    if (!confirm(`Post ${lines.length} line(s)? This writes ledger entries and closes the transfer.`)) return;
    setPosting(true);
    try {
      const { result } = await runServerFn(
        () => postTransferFn({ data: { transferId: id } }),
        "Post transfer",
      );
      const r = result as { lines_posted?: number } | null;
      toast.success(`Transferred ${r?.lines_posted ?? 0} line(s)`);
      qc.invalidateQueries({ queryKey: ["stock-transfer", id] });
      qc.invalidateQueries({ queryKey: ["stock-transfers", tenantId] });
    } catch {
      // toast handled
    } finally {
      setPosting(false);
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel this transfer?")) return;
    try {
      await runServerFn(() => cancelFn({ data: { id } }), "Cancel transfer");
      qc.invalidateQueries({ queryKey: ["stock-transfer", id] });
    } catch { /* toast handled */ }
  };

  const destroy = async () => {
    if (!confirm("Delete this transfer permanently? Lines will be removed.")) return;
    try {
      await runServerFn(() => deleteFn({ data: { id } }), "Delete transfer");
      toast.success("Transfer deleted");
      qc.invalidateQueries({ queryKey: ["stock-transfers", tenantId] });
      navigate({ to: "/app/transfers" });
    } catch { /* toast handled */ }
  };

  return (
    <>
      <PageHeader
        title={st?.reference ?? "Stock transfer"}
        description={st ? `${facMap[st.source_facility_id] ?? "—"} → ${facMap[st.dest_facility_id] ?? "—"}` : undefined}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton moduleKey="transfers" />
            <Button variant="outline" onClick={() => navigate({ to: "/app/transfers" })}>
              <ArrowLeft className="h-4 w-4 mr-2" />All transfers
            </Button>
            {editable && (
              <>
                <Button variant="outline" onClick={cancel}><X className="h-4 w-4 mr-2" />Cancel</Button>
                <Button variant="outline" onClick={destroy}><Trash className="h-4 w-4 mr-2" />Delete</Button>
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild><Button variant="outline"><Plus className="h-4 w-4 mr-2" />Add line</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Add transfer line</DialogTitle></DialogHeader>
                    <form onSubmit={addLine} className="space-y-4">
                      <div className="space-y-2">
                        <Label>SKU</Label>
                        <Select value={skuId} onValueChange={setSkuId}>
                          <SelectTrigger><SelectValue placeholder="Select SKU" /></SelectTrigger>
                          <SelectContent>{skus.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>From location</Label>
                          <Select value={srcLoc} onValueChange={setSrcLoc}>
                            <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                            <SelectContent>{srcLocs.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>To location</Label>
                          <Select value={dstLoc} onValueChange={setDstLoc}>
                            <SelectTrigger><SelectValue placeholder="Destination" /></SelectTrigger>
                            <SelectContent>{dstLocs.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2"><Label>Lot (optional)</Label><Input value={lot} onChange={(e) => setLot(e.target.value)} /></div>
                        <div className="space-y-2"><Label>Quantity</Label><Input type="number" min="1" required value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                      </div>
                      <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
                <Button onClick={post} disabled={posting || lines.length === 0}>
                  {posting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <PackageCheck className="h-4 w-4 mr-2" />Post transfer
                </Button>
              </>
            )}
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <StatusPill tone={statusTone(st?.status)} dot>{st?.status ?? "—"}</StatusPill>
          <Badge variant="outline">Lines: {lines.length}</Badge>
          <Badge variant="outline">Total qty: {lines.reduce((s, l) => s + l.qty, 0)}</Badge>
        </div>
        {st?.notes && <p className="text-sm text-muted-foreground">{st.notes}</p>}
        {editable && lines.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Note: posting requires on-hand stock at each source location. Seed inventory from the Inventory page if needed.
          </p>
        )}
        <div className="border rounded-lg bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Lot</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No lines yet.</TableCell></TableRow>
              ) : lines.map((l) => {
                const sku = skuMap[l.sku_id];
                return (
                  <TableRow key={l.id}>
                    <TableCell>{sku ? <><span className="font-mono text-xs">{sku.sku_code}</span> <span className="text-muted-foreground text-xs ml-1">{sku.name}</span></> : "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{locMap[l.source_location_id] ?? "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{locMap[l.dest_location_id] ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{l.lot ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.qty}</TableCell>
                    <TableCell className="text-right">
                      {editable && (
                        <Button variant="ghost" size="icon" onClick={() => removeLine(l.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}